"use client";

import { ReactNode, useEffect, useRef, useState } from "react";
import { PauseIcon, PlayIcon, PlusIcon } from "@phosphor-icons/react/ssr";
import { motion } from "framer-motion";
import { cn } from "~/lib/utils";
import VideoPlayer from "~/componentsDS/VideoPlayer";
import Pressable from "~/componentsDS/Pressable";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import type { GtmEvent } from "~/lib/gtm/getGtmEventData";

const ANSWER_VARIANT = {
    COMPACT: "compact",
    DEFAULT: "default",
} as const;

type AnswerVariant = typeof ANSWER_VARIANT[keyof typeof ANSWER_VARIANT];

export type AnswerItem = {
    question: string;
    answer: ReactNode;
    video: {
        mp4: string;
        hls: string;
        poster: string;
    };
}

export type AnswerVideoEvents = {
    stop: string;
    play: string;
    clickText?: {
        play: string;
        stop: string;
    };
    metaData?: Record<string, unknown>;
};

type AnswerExpandEventData = Omit<GtmEvent["value"], "meta_data"> & {
    meta_data?: Record<string, unknown>;
};

type PlaybackAnalytics = "all" | "user-initiated-only";

type Props = AnswerItem & {
    answerClassName?: string;
    expandWhenActive?: boolean;
    isActive: boolean;
    isInteracted: boolean;
    isVisible?: boolean;
    playOnlyWhenActive?: boolean;
    playbackAnalytics?: PlaybackAnalytics;
    widget?: string;
    variant?: AnswerVariant;
    videoEvents?: AnswerVideoEvents;
    eventDataOnExpand?: AnswerExpandEventData;
}

const defaultVideoEvents: AnswerVideoEvents = {
    play: "video_play",
    stop: "video_stop",
};

export const Answer = ({
    question,
    answer,
    answerClassName,
    expandWhenActive = false,
    video,
    isActive,
    isInteracted,
    isVisible = true,
    playOnlyWhenActive = false,
    playbackAnalytics = "all",
    widget = "dr_sarah",
    variant = ANSWER_VARIANT.DEFAULT,
    videoEvents = defaultVideoEvents,
    eventDataOnExpand,
}: Props) => {
    const [videoStarted, setVideoStarted] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const userInitiatedPlaybackRef = useRef(false);
    const sendEvent = useTrackEvent();

    const getVideoMeta = (completed = false) => {
        const videoNode = videoRef.current;
        if (!videoNode) return null;

        return {
            vsl_id: video.mp4,
            video_name: question,
            vsl_duration: videoNode.duration,
            vsl_current_time: videoNode.currentTime,
            vsl_completed: completed,
            vsl_sound: !videoNode.muted,
        };
    };

    const trackPlay = () => {
        const meta = getVideoMeta(false);
        if (!meta) return;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: videoEvents.play,
                widget,
                meta_data: {
                    ...meta,
                    ...(videoEvents.clickText && {
                        click_text: videoEvents.clickText.play,
                    }),
                    ...videoEvents.metaData,
                },
            },
        });
    };

    const trackStop = (completed = false) => {
        const meta = getVideoMeta(completed);
        if (!meta) return;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: videoEvents.stop,
                widget,
                meta_data: {
                    ...meta,
                    ...(videoEvents.clickText && {
                        click_text: videoEvents.clickText.stop,
                    }),
                    ...videoEvents.metaData,
                },
            },
        });
    };

    const onTogglePlay = () => {
        const video = videoRef.current;
        if (!video || (playOnlyWhenActive && !isActive)) return;

        userInitiatedPlaybackRef.current = true;

        if (video.paused) {
            video.play();
        } else {
            video.pause();
        }
    };

    const shouldTrackPlayback = () =>
        playbackAnalytics === "all" || userInitiatedPlaybackRef.current;

    const onVideoPlay = () => {
        setVideoStarted(true);

        if (shouldTrackPlayback()) {
            trackPlay();
        }

        userInitiatedPlaybackRef.current = false;
    };

    const onVideoPause = () => {
        setVideoStarted(false);
        if (videoRef.current?.ended) return;

        if (shouldTrackPlayback()) {
            trackStop(false);
        }

        userInitiatedPlaybackRef.current = false;
    };

    const onVideoEnded = () => {
        setVideoStarted(false);

        if (shouldTrackPlayback()) {
            trackStop(true);
        }

        userInitiatedPlaybackRef.current = false;
    };

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (isActive) {
            if (isInteracted) video.play().catch(console.warn);
        } else if (!video.paused) {
            video.pause();
        }
    }, [isActive, isInteracted]);

    useEffect(() => {
        if (isVisible) return;
        const video = videoRef.current;
        if (video && !video.paused) video.pause();
    }, [isVisible]);

    useEffect(() => {
        if (
            isActive &&
            (variant === ANSWER_VARIANT.DEFAULT || expandWhenActive)
        ) {
            setIsExpanded(true);
        }
    }, [expandWhenActive, isActive, variant]);

    const onToggleExpand = (e: React.MouseEvent) => {
        e.stopPropagation();

        if (!isExpanded && eventDataOnExpand) {
            sendEvent({
                event: "user_interaction",
                value: {
                    ...eventDataOnExpand,
                    meta_data: {
                        click_text: question,
                        ...eventDataOnExpand.meta_data,
                    },
                },
            });
        }

        setIsExpanded((prev) => !prev);
    };

    return (
        <Pressable className="group/outer min-h-full flex flex-col" onActivate={onTogglePlay}>
            <div
                className={cn(
                    "group/inner relative",
                    variant === ANSWER_VARIANT.COMPACT ? "mb-2" : "mb-4"
                )}
            >
                <VideoPlayer
                    ref={videoRef}
                    className={cn(
                        "rounded-2xl duration-300",
                        variant === ANSWER_VARIANT.COMPACT
                            ? "aspect-[3/4] h-auto w-full object-cover md:aspect-[352/580]"
                            : "aspect-[404/580]",
                        isActive
                            ? "opacity-100"
                            : "opacity-70 group-hover/outer:opacity-100"
                    )}
                    onPlay={onVideoPlay}
                    onPause={onVideoPause}
                    onEnded={onVideoEnded}
                    title={question}
                    preload="none"
                    {...video}
                    playsInline
                />

                <button
                    type="button"
                    aria-label={`${videoStarted ? "Pause" : "Play"} video: ${question}, with Dr. Sarah Daccarett, MD`}
                    className={cn(
                        "w-14 h-14",
                        "flex items-center justify-center",
                        "border border-primary100 group-hover/inner:border-primary200 bg-pureWhite/25 group-hover/inner:bg-primary200 backdrop-blur-md rounded-full",
                        "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
                        "transition duration-300 ease-in-out",
                        videoStarted ? "opacity-0 group-hover/outer:animate-[fade-out_2s_forwards]" : "",
                    )}
                >
                    {videoStarted ? <PauseIcon weight="fill" size={28} fill="white" /> : <PlayIcon weight="fill" size={28} fill="white" /> }
                </button>
            </div>

            <div
                className={cn(
                    "rounded-2xl px-4 text-primary500 transition-colors duration-300",
                    variant === ANSWER_VARIANT.COMPACT ? "bg-primary100 py-2" : "pb-4 pt-6",
                    variant === ANSWER_VARIANT.DEFAULT &&
                        (isActive
                            ? "bg-primary100"
                            : "bg-gray100 group-hover/outer:bg-primary100")
                )}
            >
                <button
                    type="button"
                    onClick={onToggleExpand}
                    className={cn(
                        "flex w-full justify-between text-left font-suisseBP text-[18px] font-medium leading-6",
                        variant === ANSWER_VARIANT.COMPACT
                            ? "min-h-[55px] items-start py-3"
                            : "min-h-14 pb-2"
                    )}
                >
                    <TextWithRegisteredMark as="h3" text={question} />
                    <PlusIcon className={cn("duration-200 ml-[10px] shrink-0", { "rotate-45": isExpanded })} size={24} />
                </button>

                <motion.div
                    initial={false}
                    animate={{
                        gridTemplateRows: isExpanded ? "1fr" : "0fr",
                    }}
                    transition={{
                        duration: isInteracted ? 0.3 : 0,
                        ease: "easeInOut",
                    }}
                    className="grid overflow-hidden"
                >
                    <motion.div
                        animate={{
                            opacity: isExpanded ? 1 : 0,
                            y: isExpanded ? 0 : -6,
                        }}
                        transition={{
                            duration: isInteracted ? 0.2 : 0,
                            ease: "easeOut",
                        }}
                        className="min-h-0 overflow-hidden"
                    >
                        <Paragraph className={cn("pt-2", isExpanded ? "min-h-[154px]" : "", answerClassName)}>
                            {typeof answer === "string" ? <TextWithRegisteredMark text={answer} /> : answer}
                        </Paragraph>
                    </motion.div>
                </motion.div>
            </div>
        </Pressable>
    );
};

export default Answer;
